// ============================================================
// TVPowerTab Component for Schedules.js
// ============================================================
// 
// DATA MODEL:
//   Plugs come from:     users/{userId}/tvPower/config  → plugs[] array
//   Schedules saved to:  users/{userId}/tvPower/schedules/{plugMac}
//   Screen name lookup:  screens collection (for display only)
//
// USAGE IN SCHEDULES.JS:
//   1. Add these imports at the top of Schedules.js:
//
//      import { getDoc, setDoc } from 'firebase/firestore';  // add getDoc, setDoc
//      import { Tv, Plug, Calendar, Clock, Power, Save, AlertTriangle } from 'lucide-react';
//
//   2. Place <TVPowerTab /> in your JSX where activeTab === 'tvpower':
//
//      {activeTab === 'tvpower' && <TVPowerTab userId={currentUser?.uid} />}
//
//   3. The tab toggle buttons go in the header (you already have these).
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { Plug, Power, Save, Trash2, X } from 'lucide-react';

const DAYS_OF_WEEK = [
  { key: 'mon', label: 'M', full: 'Mon' },
  { key: 'tue', label: 'T', full: 'Tue' },
  { key: 'wed', label: 'W', full: 'Wed' },
  { key: 'thu', label: 'T', full: 'Thu' },
  { key: 'fri', label: 'F', full: 'Fri' },
  { key: 'sat', label: 'S', full: 'Sat' },
  { key: 'sun', label: 'S', full: 'Sun' },
];

const DEFAULT_SCHEDULE = {
  enabled: false,
  timezone: 'America/New_York',
  blocks: [
    {
      id: 1,
      startTime: '06:00',
      endTime: '21:00',
      days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
    },
  ],
};

function TVPowerTab() {
  const { currentUser } = useAuth();
  const userId = currentUser?.uid;

  // Plug list from users/{userId}/tvPower/config
  const [plugs, setPlugs] = useState([]);
  const [selectedPlugMac, setSelectedPlugMac] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMobileSheet, setShowMobileSheet] = useState(false);

  // Screen name mapping for display (plugMac → screenName)
  const [screenNames, setScreenNames] = useState({});

  // Track which plugs have an enabled schedule (plugMac → boolean)
  const [activeSchedules, setActiveSchedules] = useState({});

  // Schedule for selected plug
  const [schedule, setSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // ─── Fetch plugs from tvPower/config ───────────────────────
  useEffect(() => {
    if (!userId) return;

    const fetchPlugs = async () => {
      setLoading(true);
      try {
        const configRef = doc(db, 'users', userId, 'tvPower', 'config');
        const configSnap = await getDoc(configRef);

        if (configSnap.exists()) {
          const data = configSnap.data();
          const plugsList = data.plugs || [];
          setPlugs(plugsList);

          // Auto-select first plug
          if (plugsList.length > 0 && !selectedPlugMac) {
            setSelectedPlugMac(plugsList[0].mac);
          }
        } else {
          setPlugs([]);
        }
      } catch (error) {
        console.error('Error fetching tvPower config:', error);
        setPlugs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPlugs();
  }, [userId, selectedPlugMac]);

  // ─── Fetch screen names using assignments map from tvPower/config ───
  // assignments map: { "PLUG_MAC": "screenDocId", ... }
  // We read the assignments, then look up each screen doc to get its name.
  useEffect(() => {
    if (!userId) return;

    const fetchScreenNames = async () => {
      try {
        // 1. Read assignments map from tvPower config
        const configRef = doc(db, 'users', userId, 'tvPower', 'config');
        const configSnap = await getDoc(configRef);

        if (!configSnap.exists()) return;

        const assignments = configSnap.data().assignments || {};
        // assignments looks like: { "80482CA6A72A": "t9wt2JH1ZMhPfv4BPwvi" }

        const nameMap = {};

        // 2. For each plug MAC → screen ID, look up the screen name
        for (const [plugMac, screenId] of Object.entries(assignments)) {
          if (!screenId) continue;
          try {
            const screenDoc = await getDoc(doc(db, 'screens', screenId));
            if (screenDoc.exists()) {
              const screenData = screenDoc.data();
              nameMap[plugMac] = screenData.screenName || screenData.name || `Screen ${screenId.slice(0, 6)}`;
            }
          } catch (err) {
            console.error(`Error fetching screen ${screenId}:`, err);
          }
        }

        setScreenNames(nameMap);
      } catch (error) {
        console.error('Error fetching screen names:', error);
      }
    };

    fetchScreenNames();
  }, [userId]);

  // ─── Fetch which plugs have an enabled schedule (for sidebar dot) ───
  useEffect(() => {
    if (!userId) return;

    const fetchActiveSchedules = async () => {
      try {
        const schedRef = doc(db, 'users', userId, 'tvPower', 'schedules');
        const schedSnap = await getDoc(schedRef);

        if (schedSnap.exists()) {
          const allSchedules = schedSnap.data();
          const statusMap = {};
          for (const [plugMac, sched] of Object.entries(allSchedules)) {
            // Only mark as active if enabled AND has at least one block
            if (sched && sched.enabled && sched.blocks && sched.blocks.length > 0) {
              statusMap[plugMac] = true;
            }
          }
          setActiveSchedules(statusMap);
        }
      } catch (error) {
        console.error('Error fetching schedule statuses:', error);
      }
    };

    fetchActiveSchedules();
  }, [userId]);

  // ─── Load schedule when a plug is selected ─────────────────
  const loadSchedule = useCallback(async (plugMac) => {
    if (!userId || !plugMac) return;

    setScheduleLoading(true);
    setHasChanges(false);
    setSaveSuccess(false);

    try {
      const schedRef = doc(db, 'users', userId, 'tvPower', 'schedules');
      const schedSnap = await getDoc(schedRef);

      if (schedSnap.exists()) {
        const allSchedules = schedSnap.data();
        if (allSchedules[plugMac]) {
          // Deep clone to avoid mutation issues
          const loaded = JSON.parse(JSON.stringify(allSchedules[plugMac]));
          setSchedule(loaded);
        } else {
          setSchedule(JSON.parse(JSON.stringify(DEFAULT_SCHEDULE)));
        }
      } else {
        setSchedule(JSON.parse(JSON.stringify(DEFAULT_SCHEDULE)));
      }
    } catch (error) {
      console.error('Error loading schedule:', error);
      setSchedule(JSON.parse(JSON.stringify(DEFAULT_SCHEDULE)));
    } finally {
      setScheduleLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (selectedPlugMac) {
      loadSchedule(selectedPlugMac);
    }
  }, [selectedPlugMac, loadSchedule]);

  // ─── Save schedule ─────────────────────────────────────────
  const handleSave = async () => {
    if (!userId || !selectedPlugMac || !schedule) return;

    setSaving(true);
    try {
      const schedRef = doc(db, 'users', userId, 'tvPower', 'schedules');
      const schedSnap = await getDoc(schedRef);
      const existing = schedSnap.exists() ? schedSnap.data() : {};

      await setDoc(schedRef, {
        ...existing,
        [selectedPlugMac]: {
          ...schedule,
          updatedAt: Date.now(),
        },
      });

        setHasChanges(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);

      // Update sidebar dot in real-time
      setActiveSchedules((prev) => {
        const updated = { ...prev };
        if (schedule.enabled && schedule.blocks && schedule.blocks.length > 0) {
          updated[selectedPlugMac] = true;
        } else {
          delete updated[selectedPlugMac];
        }
        return updated;
      });

      // Initialize the Cloud Tasks schedule chain
      if (schedule.enabled) {
      try {
        await fetch(
          `${process.env.REACT_APP_CLOUD_FUNCTIONS_URL}/wyzeInitSchedule`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userId,
              plugMac: selectedPlugMac,
            }),
          }
        );
      } catch (initError) {
        console.error('Error initializing schedule:', initError);
        // Don't block the save success - schedule init can be retried
      }
    }
    } catch (error) {
      console.error('Error saving schedule:', error);
      alert('Failed to save schedule. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ─── Schedule mutation helpers ─────────────────────────────
  const updateSchedule = (updates) => {
    setSchedule((prev) => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  const updateBlock = (blockId, field, value) => {
    setSchedule((prev) => ({
      ...prev,
      blocks: prev.blocks.map((b) =>
        b.id === blockId ? { ...b, [field]: value } : b
      ),
    }));
    setHasChanges(true);
  };

  const toggleBlockDay = (blockId, day) => {
    setSchedule((prev) => ({
      ...prev,
      blocks: prev.blocks.map((b) => {
        if (b.id !== blockId) return b;
        const days = b.days.includes(day)
          ? b.days.filter((d) => d !== day)
          : [...b.days, day];
        return { ...b, days };
      }),
    }));
    setHasChanges(true);
  };

  const addBlock = () => {
    const maxId = schedule.blocks.reduce((max, b) => Math.max(max, b.id), 0);
    setSchedule((prev) => ({
      ...prev,
      blocks: [
        ...prev.blocks,
        {
          id: maxId + 1,
          startTime: '08:00',
          endTime: '17:00',
          days: ['mon', 'tue', 'wed', 'thu', 'fri'],
        },
      ],
    }));
    setHasChanges(true);
  };

  const removeBlock = (blockId) => {
    setSchedule((prev) => ({
      ...prev,
      blocks: prev.blocks.filter((b) => b.id !== blockId),
    }));
    setHasChanges(true);
  };

  // ─── Helper: format time for display ───────────────────────
  const formatTime12 = (time24) => {
    if (!time24) return '';
    const [h, m] = time24.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  // ─── Helper: generate weekly preview bar ───────────────────
  const renderWeekPreview = () => {
    if (!schedule || !schedule.blocks.length) return null;

    const dayGroups = {};
    DAYS_OF_WEEK.forEach((d) => {
      dayGroups[d.key] = [];
    });

    schedule.blocks.forEach((block) => {
      block.days.forEach((day) => {
        if (dayGroups[day]) {
          dayGroups[day].push(block);
        }
      });
    });

    return (
      <div style={{ marginTop: '24px' }}>
        <h4
          style={{
            fontSize: '13px',
            fontWeight: '600',
            color: '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '12px',
          }}
        >
          Weekly Preview
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {DAYS_OF_WEEK.map((day) => (
            <div
              key={day.key}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <span
                style={{
                  width: '36px',
                  fontSize: '11px',
                  color: '#9ca3af',
                  fontWeight: '500',
                }}
              >
                {day.full}
              </span>
              <div
                style={{
                  flex: 1,
                  height: '16px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '4px',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {dayGroups[day.key].map((block, i) => {
                  const [sh, sm] = block.startTime.split(':').map(Number);
                  const [eh, em] = block.endTime.split(':').map(Number);
                  const startMin = sh * 60 + sm;
                  const endMin = eh * 60 + em;
                  const left = (startMin / 1440) * 100;
                  const width = ((endMin - startMin) / 1440) * 100;

                  return (
                    <div
                      key={i}
                      style={{
                        position: 'absolute',
                        left: `${left}%`,
                        width: `${Math.max(width, 1)}%`,
                        top: '2px',
                        bottom: '2px',
                        backgroundColor: schedule.enabled ? '#10b981' : '#9ca3af',
                        borderRadius: '2px',
                        opacity: 0.8,
                      }}
                      title={`${formatTime12(block.startTime)} – ${formatTime12(block.endTime)}`}
                    />
                  );
                })}
              </div>
            </div>
          ))}
          {/* Time axis labels */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ width: '36px' }} />
            <div
              style={{
                flex: 1,
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '9px',
                color: '#d1d5db',
              }}
            >
              <span>12A</span>
              <span>6A</span>
              <span>12P</span>
              <span>6P</span>
              <span>12A</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ─── Mobile plug selection handler ──────────────────────────
  const handleMobilePlugSelect = (plug) => {
    setSelectedPlugMac(plug.mac);
    setShowMobileSheet(true);
  };

  // ─── Reusable schedule editor (used in desktop panel + mobile bottom sheet) ──
  const renderScheduleEditor = () => {
    if (scheduleLoading) {
      return <div style={{ display: 'flex', justifyContent: 'center', padding: '40px', color: '#9ca3af' }}>Loading schedule...</div>;
    }
    if (!schedule) {
      return <div style={{ display: 'flex', justifyContent: 'center', padding: '40px', color: '#9ca3af' }}>Select a plug to configure its schedule.</div>;
    }

    const selectedPlug = plugs.find((p) => p.mac === selectedPlugMac);

    return (
      <>
        {/* Plug header + enable toggle */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid #f3f4f6' }}>
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Power size={18} />
              {screenNames[selectedPlugMac] || selectedPlug?.nickname || selectedPlugMac}
            </h3>
            {screenNames[selectedPlugMac] && (
              <p style={{ fontSize: '13px', color: '#9ca3af', margin: '4px 0 0 0' }}>
                ⚡ {selectedPlug?.nickname || selectedPlugMac}
              </p>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '13px', fontWeight: '500', color: schedule.enabled ? '#10b981' : '#9ca3af' }}>
              {schedule.enabled ? 'Schedule Active' : 'Schedule Off'}
            </span>
            <div onClick={() => updateSchedule({ enabled: !schedule.enabled })} role="button" tabIndex={0} style={{ width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer', backgroundColor: schedule.enabled ? '#10b981' : '#d1d5db', position: 'relative', transition: 'background-color 0.2s' }}>
              <div style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#fff', position: 'absolute', top: '3px', left: schedule.enabled ? '23px' : '3px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', pointerEvents: 'none' }} />
            </div>
          </div>
        </div>

        {/* Timezone selector */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Timezone</label>
          <select value={schedule.timezone} onChange={(e) => updateSchedule({ timezone: e.target.value })} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', color: '#374151', backgroundColor: '#fff', cursor: 'pointer', outline: 'none' }}>
            <option value="America/New_York">Eastern (ET)</option>
            <option value="America/Chicago">Central (CT)</option>
            <option value="America/Denver">Mountain (MT)</option>
            <option value="America/Los_Angeles">Pacific (PT)</option>
            <option value="America/Anchorage">Alaska (AKT)</option>
            <option value="Pacific/Honolulu">Hawaii (HT)</option>
          </select>
        </div>

        {/* Time blocks */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <h4 style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>Time Blocks</h4>
            <span onClick={addBlock} role="button" tabIndex={0} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', borderRadius: '6px', border: 'none', backgroundColor: 'transparent', fontSize: '13px', fontWeight: '600', color: '#E63946', cursor: 'pointer' }}>+ Add Block</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {schedule.blocks.map((block) => (
              <div key={block.id} style={{ padding: '16px', borderRadius: '10px', border: '1px solid #e5e7eb', backgroundColor: '#fafafa' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>ON</span>
                  <input type="time" value={block.startTime} onChange={(e) => updateBlock(block.id, 'startTime', e.target.value)} onClick={(e) => e.stopPropagation()} style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #86efac', backgroundColor: '#f0fdf4', fontSize: '14px', color: '#166534', outline: 'none', cursor: 'text', width: '130px', WebkitAppearance: 'none', MozAppearance: 'none' }} />
                  <span style={{ color: '#9ca3af', fontSize: '13px' }}>→</span>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>OFF</span>
                  <input type="time" value={block.endTime} onChange={(e) => updateBlock(block.id, 'endTime', e.target.value)} onClick={(e) => e.stopPropagation()} style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #fca5a5', backgroundColor: '#fef2f2', fontSize: '14px', color: '#991b1b', outline: 'none', cursor: 'text', width: '130px', WebkitAppearance: 'none', MozAppearance: 'none' }} />
                  {schedule.blocks.length > 1 && (
                    <span onClick={() => removeBlock(block.id)} role="button" tabIndex={0} style={{ marginLeft: 'auto', padding: '6px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', color: '#9ca3af', borderRadius: '4px', display: 'inline-flex' }} title="Remove block">
                      <Trash2 size={16} />
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {DAYS_OF_WEEK.map((day) => {
                    const active = block.days.includes(day.key);
                    return (
                      <div key={day.key} onClick={() => toggleBlockDay(block.id, day.key)} role="button" tabIndex={0} style={{ width: '34px', height: '34px', borderRadius: '50%', border: active ? 'none' : '1px solid #d1d5db', backgroundColor: active ? '#E63946' : '#fff', color: active ? '#fff' : '#9ca3af', fontSize: '13px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center', userSelect: 'none' }}>
                        {day.label}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Weekly preview */}
        {renderWeekPreview()}

        {/* Save button */}
        <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div onClick={hasChanges && !saving ? handleSave : undefined} role="button" tabIndex={0} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 18px', borderRadius: '8px', border: 'none', backgroundColor: !hasChanges ? '#d1d5db' : saving ? '#fca5a5' : '#E63946', color: '#fff', fontSize: '13px', fontWeight: '500', cursor: hasChanges && !saving ? 'pointer' : 'default', transition: 'background-color 0.2s', userSelect: 'none' }}>
            <Save size={16} />
            {saving ? 'Saving...' : 'Save Schedule'}
          </div>
          {saveSuccess && <span style={{ fontSize: '13px', color: '#10b981', fontWeight: '500' }}>✓ Saved</span>}
          {hasChanges && !saveSuccess && <span style={{ fontSize: '12px', color: '#f59e0b' }}>Unsaved changes</span>}
        </div>
      </>
    );
  };

  // ─── Loading state ─────────────────────────────────────────
  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '60px 20px',
          color: '#9ca3af',
        }}
      >
        Loading smart plugs...
      </div>
    );
  }

  // ─── No plugs found ────────────────────────────────────────
  if (plugs.length === 0) {
    return (
      <div style={{ display: 'flex', gap: '20px' }}>
        {/* Empty sidebar */}
        <div
          style={{
            width: '280px',
            flexShrink: 0,
            backgroundColor: '#fff',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            padding: '20px',
          }}
        >
          <h3
            style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#E63946',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '16px',
            }}
          >
            Screens with Smart Plugs
          </h3>
          <p
            style={{
              fontSize: '14px',
              color: '#9ca3af',
              textAlign: 'center',
              padding: '20px 0',
            }}
          >
            No smart plugs assigned.
            <br />
            Set up in TV Power settings.
          </p>
        </div>

        {/* Empty right panel */}
        <div
          style={{
            flex: 1,
            backgroundColor: '#fff',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            padding: '40px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <p style={{ color: '#9ca3af', fontSize: '15px' }}>
            No screens have smart plugs assigned. Set up plugs in TV Power
            settings first.
          </p>
        </div>
      </div>
    );
  }

  // ─── Main render ───────────────────────────────────────────
  const selectedPlug = plugs.find((p) => p.mac === selectedPlugMac);

  return (
    <>
      {/* ── Desktop: side-by-side layout ── */}
      <div className="sidebar-layout desktop-only" style={{ display: 'flex', gap: '20px' }}>
        <div style={{ width: '280px', flexShrink: 0, backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px' }}>
          <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#E63946', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '16px' }}>Screens with Smart Plugs</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {plugs.map((plug) => {
              const isSelected = selectedPlugMac === plug.mac;
              const assignedScreen = screenNames[plug.mac];
              return (
                <div key={plug.mac} onClick={() => setSelectedPlugMac(plug.mac)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedPlugMac(plug.mac); }} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px', border: 'none', cursor: 'pointer', textAlign: 'left', width: '100%', backgroundColor: isSelected ? 'rgba(230,57,70,0.08)' : 'transparent', transition: 'background-color 0.15s', userSelect: 'none' }} onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }} onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}>
                  <Plug size={16} style={{ color: isSelected ? '#E63946' : '#6b7280', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: isSelected ? '600' : '500', color: isSelected ? '#E63946' : '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{assignedScreen || plug.nickname || plug.mac}</div>
                    {assignedScreen && <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '1px' }}>{plug.nickname || plug.mac}</div>}
                  </div>
                  {activeSchedules[plug.mac] && <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10b981', flexShrink: 0 }} title="Schedule active" />}
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ flex: 1, backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '24px' }}>
          {renderScheduleEditor()}
        </div>
      </div>

      {/* ── Mobile: Plug list ── */}
      <div className="mobile-only">
        <div className="mobile-list">
          {plugs.map((plug) => {
            const assignedScreen = screenNames[plug.mac];
            return (
              <div key={plug.mac} className="mobile-list-item" onClick={() => handleMobilePlugSelect(plug)}>
                <Plug size={16} style={{ color: '#6b7280', flexShrink: 0 }} />
                <div className="mobile-list-content">
                  <div className="mobile-list-title">{assignedScreen || plug.nickname || plug.mac}</div>
                  <div className="mobile-list-subtitle">Smart Power</div>
                </div>
                {activeSchedules[plug.mac] && <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10b981', flexShrink: 0, marginRight: '8px' }} />}
              </div>
            );
          })}
          {plugs.length === 0 && <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>No smart plugs assigned. Set up in TV Power settings.</div>}
        </div>
      </div>

      {/* ── Mobile: Bottom Sheet ── */}
      {showMobileSheet && selectedPlugMac && (
        <div className="mobile-only">
          <div className="bottom-sheet-overlay" onClick={() => setShowMobileSheet(false)} />
          <div className="bottom-sheet">
            <div className="bottom-sheet-header">
              <h3>{screenNames[selectedPlugMac] || selectedPlug?.nickname || selectedPlugMac}</h3>
              <button className="bottom-sheet-close" onClick={() => setShowMobileSheet(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="bottom-sheet-content">
              {renderScheduleEditor()}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default TVPowerTab;